import { Component } from '@theme/component';

/**
 * Expandable text block component.
 *
 * Reads `data-word-limit` to decide whether truncation is needed.
 * When the stripped text content exceeds the limit, a truncated plain-text
 * preview is shown with a configurable "Read More" / "Read Less" toggle.
 * The full rich-text content is preserved and revealed on expand.
 *
 * @typedef {object} ExpandableTextRefs
 * @property {HTMLElement} fullContent - Container holding the full richtext content.
 * @property {HTMLElement} previewContent - Container that receives the truncated plain-text preview.
 * @property {HTMLButtonElement} toggle - The Read More / Read Less button.
 *
 * @extends {Component<ExpandableTextRefs>}
 */
class ExpandableTextBlock extends Component {
  requiredRefs = ['fullContent', 'previewContent', 'toggle'];

  /** @type {Animation | undefined} */
  #animation;

  get #wordLimit() {
    return parseInt(this.dataset.wordLimit ?? '0', 10);
  }

  get #readMoreText() {
    return this.dataset.readMore ?? 'Read More';
  }

  get #readLessText() {
    return this.dataset.readLess ?? 'Read Less';
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initTruncation();
  }

  #initTruncation() {
    const wordLimit = this.#wordLimit;

    if (!wordLimit) return;

    const { fullContent, previewContent, toggle } = this.refs;
    const fullText = fullContent.textContent?.trim() ?? '';
    const words = fullText.split(/\s+/);

    if (words.length <= wordLimit) return;

    const truncated = words.slice(0, wordLimit).join(' ') + '\u2026';

    previewContent.textContent = truncated;

    fullContent.style.height = '0';
    fullContent.style.overflow = 'hidden';
    previewContent.hidden = false;

    toggle.hidden = false;
    toggle.textContent = this.#readMoreText;
    toggle.setAttribute('aria-expanded', 'false');
  }

  /**
   * Toggles between the collapsed preview and the full expanded content.
   */
  handleToggle() {
    const isExpanded = this.refs.toggle.getAttribute('aria-expanded') === 'true';
    isExpanded ? this.#collapse() : this.#expand();
  }

  #expand() {
    const { previewContent, fullContent, toggle } = this.refs;

    this.#animation?.cancel();

    const endHeight = fullContent.scrollHeight;

    previewContent.hidden = true;

    this.#animation = fullContent.animate(
      { height: ['0px', `${endHeight}px`], opacity: [0, 1] },
      { duration: 300, easing: 'ease-in-out' }
    );

    toggle.setAttribute('aria-expanded', 'true');
    toggle.textContent = this.#readLessText;

    this.#animation.onfinish = () => {
      fullContent.style.height = 'auto';
      fullContent.style.overflow = '';
    };
  }

  #collapse() {
    const { previewContent, fullContent, toggle } = this.refs;

    this.#animation?.cancel();

    const startHeight = fullContent.offsetHeight;

    fullContent.style.height = `${startHeight}px`;
    fullContent.style.overflow = 'hidden';

    this.#animation = fullContent.animate(
      { height: [`${startHeight}px`, '0px'], opacity: [1, 0] },
      { duration: 300, easing: 'ease-in-out' }
    );

    toggle.setAttribute('aria-expanded', 'false');
    toggle.textContent = this.#readMoreText;

    this.#animation.onfinish = () => {
      fullContent.style.height = '0';
      previewContent.hidden = false;
    };
  }
}

customElements.define('expandable-text-block', ExpandableTextBlock);
